import { Link } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum } from "@tenant/ui";
import clsx from "clsx";

import styles from "./OffersStatusScreen.module.scss";

import MaintenanceIllustration from "@/assets/icons/ui/maintenance.svg?react";
import HotelBedIllustration from "@/assets/images/hotel-bed.svg?react";

export type OffersStatusVariant = "no-availability" | "error";

export interface OffersStatusScreenProps {
  variant: OffersStatusVariant;
  location?: string;
  searchAllStaysHref?: string;
  onSearchAllStays?: () => void;
  className?: string;
}

export const OffersStatusScreen = ({
  variant,
  location,
  searchAllStaysHref,
  onSearchAllStays,
  className,
}: OffersStatusScreenProps) => {
  const { t } = useI18nContext();
  const isError = variant === "error";
  const [block, cn] = useModuleBEM(styles, "OffersStatusScreen", {
    error: isError,
  });

  const content = isError
    ? {
        illustration: <MaintenanceIllustration aria-hidden="true" />,
        title: t("hotelFundedOffersPage.errorState.title"),
        subtitle: t("hotelFundedOffersPage.errorState.subtitle"),
        actionLabel: t("hotelFundedOffersPage.errorState.cta"),
      }
    : {
        illustration: <HotelBedIllustration aria-hidden="true" />,
        title: t("hotelFundedOffersPage.noAvailability.title"),
        subtitle: t("hotelFundedOffersPage.noAvailability.subtitle", {
          location,
        }),
        actionLabel: t("hotelFundedOffersPage.noAvailability.cta"),
      };

  return (
    <div className={clsx(block, className)} role={isError ? "alert" : "status"}>
      {content.illustration}
      <h2 className={cn("title")}>{content.title}</h2>
      <p className={cn("subtitle")}>{content.subtitle}</p>
      <div className={cn("actions")}>
        {isError ? (
          <Button
            className={cn("action")}
            buttonType={ButtonTypeEnum.Primary}
            onClick={() => window.location.reload()}
          >
            {content.actionLabel}
          </Button>
        ) : (
          <Link
            className={cn("action")}
            to={searchAllStaysHref ?? "/"}
            onClick={onSearchAllStays}
          >
            {content.actionLabel}
          </Link>
        )}
      </div>
    </div>
  );
};
